import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { userService } from '../../../_services';
import AssignAsGrandParent from './AssignAsGrandparent.jsx';
import * as z from 'zod';
import { US_STATES } from '../../../utils/constants';

const editUserSchema = z.object({
    UserName: z.string()
        .min(1, 'Username is required')
        .refine(val => val.length >= 5, 'User Name must be at least 5 characters long')
        .refine(val => !val.includes(' '), 'User Name does not allow space')
        .refine(val => !/^[0-9]+$/.test(val), 'This Username not allowed')
        .refine(val => !/user|rxh|temp/i.test(val), 'This Username not allowed'),
    Fname: z.string()
        .min(1, 'Please enter valid Full Name')
        .refine(val => val.trim().length >= 3, 'Full name must be at least 3 characters long'),
    Phone: z.string()
        .min(1, 'Phone Number is required')
        .refine(val => val.replace(/[- )(]/g, '').length === 10, 'Phone Number must be 10 digits'),
    Commission: z.string()
        .min(1, 'Commission is required')
        .refine(val => parseFloat(val) >= 0.01, 'Please enter valid commission')
        .refine(val => parseFloat(val) <= 10, 'Please input valid commission')
        .refine(val => /^\d+\.?\d{0,2}$/.test(val), 'Please enter amount upto two decimal'),
    OverrideCommission: z.string().optional()
        .refine(val => !val || parseFloat(val) >= 0.01, 'Please enter valid override commission')
        .refine(val => !val || parseFloat(val) <= 2.75, 'Comm. exceeding Wholesaler max Comm.'),
    SecLevelCommission: z.string()
        .min(1, '2nd Level Commission is required')
        .refine(val => parseFloat(val) >= 0.01, 'Please enter valid 2nd level commission')
        .refine(val => parseFloat(val) <= 10, 'Please input valid commission')
        .refine(val => /^\d+\.?\d{0,2}$/.test(val), 'Please enter amount upto two decimal'),
});

// US_STATES imported from utils/constants

function EditUserDialog({ open, onOpenChange, repid, onSuccess }) {
    const [formData, setFormData] = useState({
        UserName: '',
        Fname: '',
        Phone: '',
        Email: '',
        Address1: '',
        Country: 'United States',
        State: '',
        City: '',
        Zip: '',
        group_no: '',
        CreatedOn: '',
        Commission: '',
        OverrideCommission: '',
        SecLevelCommission: '',
        AllowForGC: false,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [grandparentPopup, setGrandparentPopup] = useState(false);
    const [grandparentsList, setGrandparentsList] = useState([]);
    const [gpLoading, setGpLoading] = useState(false);
    const [secondLevelValue, setSecondLevelValue] = useState(false);

    // Guard against duplicate/concurrent API calls when toggling AllowForGC
    const gpFetchInProgress = useRef(false);
    // Ref to always hold the latest loaded rep username — avoids stale-closure issues
    const repUserNameRef = useRef('');

    useEffect(() => {
        if (open && repid) {
            fetchUserDetails();
        }
    }, [open, repid]);

    const fetchUserDetails = async () => {
        setSubmitMessage('');
        setErrors({});
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            const apidata = await userService.DetailOfUser(UserId, UserName, repid);

            if (apidata && apidata.length > 0) {
                const u = apidata[0];
                repUserNameRef.current = u.userName || '';
                setFormData({
                    UserName: u.userName || '',
                    Fname: ((u.firstName || '') + ' ' + (u.lastName || '')).trim() || u.fullName || '',
                    Phone: u.phone || u.phoneNumber || '',
                    Email: u.email || u.emailAddress || '',
                    Address1: u.address1 || u.address || '',
                    Country: u.countryName || 'United States',
                    State: u.stateName || u.state || '',
                    City: u.cityName || u.city || '',
                    Zip: u.zipCode || '',
                    group_no: u.groupNumber || '',
                    CreatedOn: u.createdOn || '',
                    Commission: u.commission || '',
                    OverrideCommission: u.overrideCommission || '',
                    SecLevelCommission: u.grandchildCommission || '0.10',
                    AllowForGC: u.isAllowedForGC || false,
                });
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            setSubmitMessage('Error loading user details');
        }
    };

    const handleInputChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setSubmitMessage('');
    };

    const validate = () => {
        const result = editUserSchema.safeParse(formData);
        if (result.success) return {};
        const fe = result.error.flatten().fieldErrors;
        return {
            ...(fe.UserName?.[0] && { UserName: fe.UserName[0] }),
            ...(fe.Fname?.[0] && { Fname: fe.Fname[0] }),
            ...(fe.Phone?.[0] && { Phone: fe.Phone[0] }),
            ...(fe.Commission?.[0] && { Commission: fe.Commission[0] }),
            ...(fe.OverrideCommission?.[0] && { OverrideCommission: fe.OverrideCommission[0] }),
            ...(fe.SecLevelCommission?.[0] && { SecLevelCommission: fe.SecLevelCommission[0] }),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitMessage('');

        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');

        try {
            // Validate phone first
            const phoneValidation = await userService.GetPhoneNoValidate(formData.Phone);
            if (phoneValidation && phoneValidation[0]?.code !== "1") {
                setErrors(prev => ({ ...prev, Phone: 'Invalid Phone Number' }));
                setLoading(false);
                return;
            }
            // Use formatted phone from API if available
            const formattedPhone = phoneValidation?.[0]?.message || formData.Phone;

            const apidata = await userService.UpdateUserDetail(
                UserName,
                UserId,
                formData.UserName,
                repid,
                formData.Fname,
                formData.Email,
                formattedPhone,
                formData.group_no,
                formData.Address1,
                '',
                formData.Country,
                formData.State,
                formData.City,
                formData.Zip,
                formData.Commission,
                formData.OverrideCommission,
                formData.SecLevelCommission,
                formData.AllowForGC
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === '1') {
                    let msg = 'Updated Successfully';
                    if (apidata.length > 1) {
                        const username = apidata[0].statusMessage;
                        const comm = apidata[1].code;
                        if (apidata[1].statusMessage === '') {
                            msg = `Commission for all groups associated with ${username} have been updated to ${comm}`;
                        } else if (apidata[1].statusMessage === 'Custom Value Set') {
                            msg = `Commission for no groups updated as all of ${username} groups have custom commission values set`;
                        } else {
                            const groupMsgs = apidata.slice(1).map(i => i.statusMessage).filter(s => s !== username);
                            msg = `Commission for some groups associated with ${username} have been updated to ${comm}. ${groupMsgs.length === 1 ? 'This group number is not updated' : 'These groups were not updated'}: ${groupMsgs.join(', ')}`;
                        }
                    }
                    setSubmitMessage(msg);
                    if (onSuccess) onSuccess();
                } else {
                    setSubmitMessage(apidata[0].statusMessage || 'Error updating user');
                }
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setSubmitMessage('Error updating user');
        } finally {
            setLoading(false);
        }
    };

    const handleSecondLevelToggle = async (checked) => {
        handleInputChange('AllowForGC', checked);
        setSecondLevelValue(checked);

        // Always fetch grandparent users on toggle (both ON and OFF) � matches dev-v3 behavior
        // Guard against duplicate/concurrent calls
        if (gpFetchInProgress.current) return;
        gpFetchInProgress.current = true;

        setGpLoading(true);
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [UserId, UserName] = Usermeta_Data.split(':');
        try {
            const apidata = await userService.UsersUnderGrandparent(UserId, UserName, repid, repUserNameRef.current);
            if (apidata && apidata.length > 0) {
                setGrandparentsList(apidata);
                setGrandparentPopup(true);
            }
        } catch (error) {
            console.error('Error fetching grandparent users:', error);
        } finally {
            setGpLoading(false);
            gpFetchInProgress.current = false;
        }
    };

    const signUpDate = formData.CreatedOn ? formData.CreatedOn.split('T')[0] : '';

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-primary">
                            Edit User
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2">
                        <div className="py-4 space-y-6">

                            {/* -- Personal -- */}
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h3 className="text-sm font-semibold text-primary uppercase tracking-wide whitespace-nowrap">Personal</h3>
                                    <Separator className="flex-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="UserName">Username *</Label>
                                        <Input
                                            id="UserName"
                                            value={formData.UserName}
                                            onChange={(e) => handleInputChange('UserName', e.target.value)}
                                            className={errors.UserName ? 'border-destructive' : ''}
                                        />
                                        {errors.UserName && <p className="text-sm text-destructive">{errors.UserName}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="Fname">Full Name *</Label>
                                        <Input
                                            id="Fname"
                                            value={formData.Fname}
                                            onChange={(e) => handleInputChange('Fname', e.target.value)}
                                            className={errors.Fname ? 'border-destructive' : ''}
                                        />
                                        {errors.Fname && <p className="text-sm text-destructive">{errors.Fname}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="Phone">Phone *</Label>
                                        <Input
                                            id="Phone"
                                            value={formData.Phone}
                                            onChange={(e) => handleInputChange('Phone', e.target.value)}
                                            className={errors.Phone ? 'border-destructive' : ''}
                                        />
                                        {errors.Phone && <p className="text-sm text-destructive">{errors.Phone}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="Email">Email</Label>
                                        <Input
                                            id="Email"
                                            type="email"
                                            value={formData.Email}
                                            disabled
                                            className="bg-muted"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Primary Group Number</Label>
                                        <Input
                                            value={formData.group_no}
                                            disabled
                                            className="bg-muted"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>User Sign Up Date</Label>
                                        <Input
                                            value={signUpDate}
                                            disabled
                                            className="bg-muted"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* -- Address -- */}
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h3 className="text-sm font-semibold text-primary uppercase tracking-wide whitespace-nowrap">Address</h3>
                                    <Separator className="flex-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2 col-span-2">
                                        <Label htmlFor="Address1">Street Address</Label>
                                        <Input
                                            id="Address1"
                                            value={formData.Address1}
                                            onChange={(e) => handleInputChange('Address1', e.target.value)}
                                            className={errors.Address1 ? 'border-destructive' : ''}
                                        />
                                        {errors.Address1 && <p className="text-sm text-destructive">{errors.Address1}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Country</Label>
                                        <Input
                                            value={formData.Country}
                                            disabled
                                            className="bg-muted"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="State">State</Label>
                                        <Select value={formData.State} onValueChange={(value) => handleInputChange('State', value)}>
                                            <SelectTrigger className={errors.State ? 'border-destructive' : ''}>
                                                <SelectValue placeholder="Select State" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {US_STATES.map((state) => (
                                                    <SelectItem key={state.value} value={state.value}>{state.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.State && <p className="text-sm text-destructive">{errors.State}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="Zip">ZIP / Postal Code</Label>
                                        <Input
                                            id="Zip"
                                            value={formData.Zip}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (/^[0-9]*$/.test(val)) handleInputChange('Zip', val);
                                            }}
                                            className={errors.Zip ? 'border-destructive' : ''}
                                        />
                                        {errors.Zip && <p className="text-sm text-destructive">{errors.Zip}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="City">City</Label>
                                        <Input
                                            id="City"
                                            value={formData.City}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (/^[a-zA-Z ]*$/.test(val)) handleInputChange('City', val);
                                            }}
                                            className={errors.City ? 'border-destructive' : ''}
                                        />
                                        {errors.City && <p className="text-sm text-destructive">{errors.City}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* -- Commission -- */}
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h3 className="text-sm font-semibold text-primary uppercase tracking-wide whitespace-nowrap">Commission</h3>
                                    <Separator className="flex-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="Commission">Commission *</Label>
                                        <Input
                                            id="Commission"
                                            type="number"
                                            step="0.01"
                                            value={formData.Commission}
                                            onChange={(e) => handleInputChange('Commission', e.target.value)}
                                            onKeyPress={(e) => { if (/[^0-9.]/.test(e.key)) e.preventDefault(); }}
                                            className={errors.Commission ? 'border-destructive' : ''}
                                        />
                                        {errors.Commission && <p className="text-sm text-destructive">{errors.Commission}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="OverrideCommission">Override Commission</Label>
                                        <Input
                                            id="OverrideCommission"
                                            type="number"
                                            step="0.01"
                                            value={formData.OverrideCommission}
                                            onChange={(e) => handleInputChange('OverrideCommission', e.target.value)}
                                            onKeyPress={(e) => { if (/[^0-9.]/.test(e.key)) e.preventDefault(); }}
                                            className={errors.OverrideCommission ? 'border-destructive' : ''}
                                        />
                                        {errors.OverrideCommission && <p className="text-sm text-destructive">{errors.OverrideCommission}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="SecLevelCommission">2nd Level Commission *</Label>
                                        <Input
                                            id="SecLevelCommission"
                                            type="number"
                                            step="0.01"
                                            value={formData.SecLevelCommission}
                                            onChange={(e) => handleInputChange('SecLevelCommission', e.target.value)}
                                            onKeyPress={(e) => { if (/[^0-9.]/.test(e.key)) e.preventDefault(); }}
                                            className={errors.SecLevelCommission ? 'border-destructive' : ''}
                                        />
                                        {errors.SecLevelCommission && <p className="text-sm text-destructive">{errors.SecLevelCommission}</p>}
                                    </div>
                                    <div className="space-y-2 flex flex-col justify-end pb-1">
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                id="AllowForGC"
                                                checked={formData.AllowForGC}
                                                onCheckedChange={handleSecondLevelToggle}
                                                disabled={gpLoading}
                                            />
                                            <Label htmlFor="AllowForGC" className="cursor-pointer font-normal">
                                                Allow for 2nd Level Commission
                                            </Label>
                                            {gpLoading && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {submitMessage && (
                            <Alert variant={submitMessage.toLowerCase().includes('error') ? 'destructive' : 'success'} className="mt-2">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{submitMessage}</AlertDescription>
                            </Alert>
                        )}

                        <DialogFooter className="mt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={loading}
                            >
                                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</> : 'Save Changes'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {grandparentPopup && (
                <AssignAsGrandParent
                    grandparentsList={grandparentsList}
                    secondLevel={secondLevelValue}
                    repUserName={formData.UserName}
                    repId={repid}
                    toggle_gp_popup={() => {
                        setGrandparentPopup(false);
                        setGrandparentsList([]);
                    }}
                />
            )}
        </>
    );
}

export default EditUserDialog;

